import React from 'react';
import intl from 'react-intl-universal';

import { OccType } from '@/config/constant';
import uuid from './uuid';
import { formatDate, seconds2Time, seconds2Minute } from './formatDate';
import storage from './storage';
import { md5, fileMd5 } from './md5';

import {
  isEmptyObject,
  listToMap,
  filterFormParams,
  // transformPriceUnit,
  getIsAdult,
} from './transform';
import { isNil } from './number';

export interface IdataSource {
  label: string;
  value: string | number;
  disabled?: boolean;
  children?: any;
}

export interface ItabItem {
  key: string;
  tab: string | React.ReactNode;
  component?: React.ReactNode;
}

export interface Ipagination {
  endRow?: number;
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;
  isFirstPage?: boolean;
  isLastPage?: boolean;
  navigateFirstPage?: number;
  navigateLastPage?: number;
  navigatePages?: number;
  navigatepageNums?: Array<number>;
  nextPage?: number;
  pageNum?: number;
  pageSize?: number;
  pages?: number;
  prePage?: number;
  size?: number;
  startRow?: number;
  total?: number;
}

export const renderIntlLabel = (str, value?) => {
  return intl.get(str, value);
};
export interface HistoryParams {
  // 入住包含入住中、续住中
  orderStatus?: 'reservation' | 'check-in' | 'cancel-reservation' | 'check-out';
  resvNo?: string;
  recNo?: string;
  tabKey?: 'guest' | 'room' | 'bill' | 'checkRoom';
}

// 获取 url 中的参数
const getUrlParam = (key: string) => {
  const reg = new RegExp(`(^|&)${key}=([^&]*)(&|$)`);
  const r = window.location.search.substring(1).match(reg);
  if (r !== null) return decodeURIComponent(r[2]);
  return '';
};

// 修改 url 中的参数
function updateUrlParam(url: string, key: string, value: string) {
  const r = new RegExp(`([?&])${key}=.*?(&|$)`, 'i');
  const separator = url.indexOf('?') !== -1 ? '&' : '?';

  if (r.test(url)) {
    return url.replace(r, `$1${key}=${value}$2`);
  }
  return `${url}${separator}${key}=${value}`;
}

// 获取 url 中的泳道标识
const getSwimTag = () => {
  const hostArr = window.location.host.match(/es-hotel-(dev|test|uat)-(\S+).wywk.cn/);
  return hostArr ? hostArr[2] : '';
};

// 获取 origin
const getOrigin = () => {
  const loca = window.location;
  const origin = loca.origin ? loca.origin : `${loca.protocol}//${loca.host}`;
  return origin;
};

// 单例模式
function singleton(className) {
  let ins;
  const TempClassName = className;
  const proxy = new Proxy(TempClassName, {
    construct(target, args) {
      if (!ins) {
        ins = new TempClassName(...args);
      }
      return ins;
    },
  });
  TempClassName.prototype.construct = proxy;
  return proxy;
}

/**
 * 按指定键值分组
 * @param arr
 * @param generateKey string || function
 * @returns obj
 */
function groupBy(arr, generateKey) {
  let tempKey = generateKey;
  // 参数归一化
  if (typeof tempKey === 'string') {
    const propsName = tempKey;
    tempKey = (item) => item[propsName];
  }
  const result = {};
  for (const item of arr) {
    const key = tempKey(item);
    if (!result[key]) {
      result[key] = [];
    }
    result[key].push(item);
  }
  return result;
}

/**
 * 按指定关键字排序后计算相同元素所跨行数
 * @param list 对象数组
 * @param key 对象key
 * @param targteKey 对象中新增的跨行key
 * @returns 新数组
 */
function calcRowSpan(list, key, targteKey) {
  let attr = [...list];
  // 先排序
  attr.sort((a, b) => a[key] - b[key]);
  // 数组反转，从后往前遍历
  attr.reverse();
  // 出现次数
  let count = 0;
  attr = attr?.map((item, index) => {
    count += 1;
    if (attr[index + 1] && attr[index + 1][key] && attr[index + 1][key] === item[key]) {
      return { ...item, [targteKey]: 0 };
    }
    const tempItem = { ...item, [targteKey]: count };
    count = 0;
    return tempItem;
  });
  return attr.reverse();
}

// 下载文件
const downloadBlobFile = (source: Blob, filename: string) => {
  return new Promise<void>((resolve, reject) => {
    try {
      const url = window.URL.createObjectURL(source);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      link.click();
      window.URL.revokeObjectURL(url);
      resolve();
    } catch (e) {
      reject();
    }
  });
};

// val 不考虑负数
const padStartZero = (val: number) => {
  return val < 10 ? `0${val}` : val;
};

const downloadFile = (source: Blob, filename: string) => {
  if (!source) return Promise.reject();

  return new Promise((resolve) => {
    const url = window.URL.createObjectURL(source);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    link.click();
    window.URL.revokeObjectURL(url);
    resolve('download success');
  });
};

// 入住类型
const getOccType = (data: { type?: OccType; multi?: boolean }) => {
  if (data.multi) {
    return intl.get('app_enum_occ_type_multi');
  }
  if (data.type === OccType.DayUse) {
    return intl.get('app_enum_occ_type_day');
  }
  if (data.type === OccType.TimeRENT) {
    return intl.get('app_enum_occ_type_time');
  }

  return '';
};

// 入住时长
const getOccDuration = (data: { type?: OccType; duration: any; multi?: boolean }) => {
  const value = data.duration;
  if (data.multi) {
    return intl.get('app_enum_occ_type_time_value', { value });
  }
  if (data.type === OccType.DayUse) {
    return intl.get('app_enum_occ_type_day_value', { value });
  }
  if (data.type === OccType.TimeRENT) {
    return intl.get('app_enum_occ_type_time_value', { value });
  }

  return '';
};

// 是否海外SaaS
function isIntl() {
  const { host } = window.location;
  // 本地调试
  if (host.includes('localhost')) return false;

  return !host.includes('.wywk.cn') && !host.includes('.wy-hotel.cn');
}

// 通过域名获取租户code和后端api域名的三级部分（前端域名的三级部分为租户code）
function getCodeInfoFromDomain(url?: string) {
  const parsedUrl = new URL(url || window.location.href);
  const domain = parsedUrl.hostname;
  const parts = domain.split('.');
  // 租户Code
  const code = isIntl() ? parts.slice(-3, -2)[0] : 'wy'; // 国内租户code wy
  // 后端api域名的三级部分
  const apiThirdLevel = code?.match(/^[a-zA-Z]+/)?.[0] ?? '';

  return {
    code,
    apiThirdLevel,
  };
}

// content fallback
const renderContent = (value, formatter?) => {
  if (isNil(value)) return '—';
  if (typeof formatter === 'function') return formatter(value);
  return value ?? '—';
};

/*
 * useful utils of wywk pms
 */
export {
  getUrlParam,
  updateUrlParam,
  uuid,
  formatDate,
  seconds2Time,
  seconds2Minute,
  storage,
  md5,
  fileMd5,
  downloadFile,
  isEmptyObject,
  listToMap,
  filterFormParams,
  getIsAdult,
  getSwimTag,
  getOrigin,
  singleton,
  groupBy,
  calcRowSpan,
  downloadBlobFile,
  padStartZero,
  getOccType,
  getOccDuration,
  getCodeInfoFromDomain,
  isIntl,
  renderContent,
};
